<script setup lang="ts">
    import { computed, onMounted, watch, nextTick, shallowReactive, ref, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import { getDistinctColor, numCompare, toPercentageString, toStringSmartly } from '@common/Common'
    import CorporateNavigation from "@common/Components/CorporateNavigation.vue"
    import ScenarioNavigation from '@Quest/CorporateScenarios/Components/ScenarioNavigation.vue'

    import { KendoChart, KendoChartDataSerie, KendoChartOptions } from '@Kendo/Components/KendoChart'

    import { useProjectsPageQueryV1 } from '@Quest/CorporateScenarios/queries-vue-composables'

    import type {
        PortfolioReadModel,
        ProjectsPageReadModel,
        SelectionProjectReadModel
    } from '@Quest/CorporateScenarios/readmodels'


    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))
    const scenarioId = computed(() => String(route.params.scenarioId ?? ''))


    // ======================
    // Query
    // ======================

    const {
        runProjectsPageQueryV1,
        modelProjectsPageQueryV1
    } = useProjectsPageQueryV1(workspaceId, strategyId, scenarioId)

    const rm = modelProjectsPageQueryV1 as Ref<ProjectsPageReadModel | null>


    // ======================
    // Local state
    // ======================

    const searchPhrase = ref<string | null>(null)

    // ======================================================
    // Charts (unchanged logic)
    // ======================================================

    class EfficientFrontierChart
    {
        isRendered: boolean = false
        Chart: KendoChart

        constructor()
        {
            const options = new KendoChartOptions()
            options.HideLegend()
            options.SetPannable(true)
            options.SetZoomable(true)
            options.SetTooltip(false, '#= dataItem.Name #')

            this.Chart = KendoChart.CreateScatterChart(options)
        }

        SetData(
            projects: SelectionProjectReadModel[],
            currencyLabel: string,
            portfolios: PortfolioReadModel[]
        )
        {
            let dataItems: {
                Id: string,
                Name: string,
                PortfolioId: string,
                PortfolioName: string,
                X: number,
                Y: number,
                Z: number,
                TooltipHtml: string
            }[] = []

            for (const p of projects)
            {
                dataItems.push({
                    Id: p.Id,
                    Name: p.Name,
                    X: p.TotalCost as number,
                    Y: p.TotalValue as number,
                    Z: p.CostOfValuePoint as number,
                    PortfolioId: p.PortfolioId,
                    PortfolioName: p.PortfolioName,
                    TooltipHtml: `${p.Name}`
                })
            }

            dataItems = dataItems
                .filter(a => a.X != null && a.Y != null && a.Z != null)
                .sort((a, b) => numCompare(a.Z, b.Z))

            for (let i = 1; i < dataItems.length; i++)
            {
                dataItems[i].X += dataItems[i - 1].X
                dataItems[i].Y += dataItems[i - 1].Y
            }

            const series: KendoChartDataSerie[] = portfolios.map(portfolio =>
            {
                const serie = new KendoChartDataSerie()
                serie.SetXField("X")
                serie.SetYField("Y")
                serie.SetCategoryField("Name")
                serie.SetColor(portfolio.Color)
                serie.SetMarkers(true, "circle")
                serie.SetData(dataItems.filter(a => a.PortfolioId === portfolio.Id))
                return serie
            })

            const options = this.Chart.GetOptions()
            options.SetTooltip(true, "#= dataItem.TooltipHtml #")

            const xMaxValue = Math.max(...dataItems.map(v => v.X))
            const yMaxValue = Math.max(...dataItems.map(v => v.Y))

            options.SetXAxis(
                null,
                0,
                xMaxValue * 1.1,
                `Cost (${currencyLabel})`,
                xMaxValue > 10 ? "{0:n0}" : null,
                2,
                true,
                true,
                null,
                xMaxValue
                    ? (e: any): string => kendo.toString(e.value / xMaxValue, "p0")
                    : undefined
            )

            options.SetYAxis(
                null,
                0,
                yMaxValue * 1.1,
                "Value",
                yMaxValue > 10 ? "{0:n0}" : null,
                true,
                true,
                null,
                yMaxValue
                    ? (e: any): string => kendo.toString(e.value / yMaxValue, "p0")
                    : undefined
            )

            this.Chart.SetOptions(options)
            this.Chart.SetDataSeries(series)
            this.Chart.UpdateOptions()
        }

        Render()
        {
            if (!this.isRendered)
            {
                this.Chart.Render1("EfficientFrontier")
                this.isRendered = true
            }
        }
    }


    class PrioritizationMatrixChart
    {
        isRendered: boolean = false
        Chart: KendoChart

        constructor()
        {
            const options = new KendoChartOptions()
            options.HideLegend()
            options.SetPannable(true)
            options.SetZoomable(true)
            options.SetTooltip(false, '#= dataItem.Name #')

            this.Chart = KendoChart.CreateScatterChart(options)
        }

        SetData(
            projects: SelectionProjectReadModel[],
            currencyLabel: string,
            portfolios: PortfolioReadModel[]
        )
        {
            let dataItems: {
                Id: string,
                Name: string,
                PortfolioId: string,
                PortfolioName: string,
                X: number,
                Y: number,
                TooltipHtml: string
            }[] = []

            for (const p of projects)
            {
                dataItems.push({
                    Id: p.Id,
                    Name: p.Name,
                    X: p.TotalCost as number,
                    Y: p.TotalValue as number,
                    PortfolioId: p.PortfolioId,
                    PortfolioName: p.PortfolioName,
                    TooltipHtml: `${p.Name}`
                })
            }

            dataItems = dataItems.filter(a => a.X != null && a.Y != null)

            const series: KendoChartDataSerie[] = portfolios.map(portfolio =>
            {
                const serie = new KendoChartDataSerie()
                serie.SetXField("X")
                serie.SetYField("Y")
                serie.SetCategoryField("Name")
                serie.SetColor(portfolio.Color)
                serie.SetMarkers(true, "circle")
                serie.SetData(dataItems.filter(a => a.PortfolioId === portfolio.Id))
                return serie
            })

            const options = this.Chart.GetOptions()
            options.SetTooltip(true, "#= dataItem.TooltipHtml #")

            const xMaxValue = Math.max(...dataItems.map(v => v.X))
            const yMaxValue = Math.max(...dataItems.map(v => v.Y))

            options.SetXAxis(
                null,
                0,
                xMaxValue * 1.1,
                `Cost (${currencyLabel})`,
                xMaxValue > 10 ? "{0:n0}" : null,
                2,
                true,
                false,
                null
            )

            options.SetYAxis(
                null,
                0,
                yMaxValue * 1.1,
                "Value",
                yMaxValue > 10 ? "{0:n0}" : null,
                true,
                false,
                null
            )

            this.Chart.SetOptions(options)
            this.Chart.SetDataSeries(series)
            this.Chart.UpdateOptions()
        }

        Render()
        {
            if (!this.isRendered)
            {
                this.Chart.Render1("PrioritizationMatrix")
                this.isRendered = true
            }
        }
    }


    const EfficientFrontier = shallowReactive(new EfficientFrontierChart())
    const PrioritizationMatrix = shallowReactive(new PrioritizationMatrixChart())


    // ======================
    // Computed
    // ======================

    const projectsView = computed<SelectionProjectReadModel[]>(() =>
    {
        let view = new Array(...(rm.value?.Projects ?? []))

        if (searchPhrase.value)
        {
            const phrase = searchPhrase.value.toLowerCase()
            view = view.filter(a => a.Name.toLowerCase().indexOf(phrase) > -1)
        }

        return view
    })


    // ======================
    // Formatters
    // ======================

    function fmtNum(val: number | null)
    {
        return toStringSmartly(val)
    }

    function fmtPct(val: number | null)
    {
        return toPercentageString(val)
    }


    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Enterprise Plan - Projects"
        runProjectsPageQueryV1()
    })


    // ======================
    // Replace onSetModel
    // ======================

    watch(rm, (current) =>
    {
        if (!current) return

        current.Portfolios.forEach((p, index) => p.Color = getDistinctColor(index))

        EfficientFrontier.SetData(current.Projects, current.CurrencyLabel, current.Portfolios)
        PrioritizationMatrix.SetData(current.Projects, current.CurrencyLabel, current.Portfolios)

        nextTick(() =>
        {
            EfficientFrontier.Render()
            PrioritizationMatrix.Render()
        })
    })


    // ======================
    // Replace onScheduled (query only version)
    // ======================

    function onScheduled()
    {
        runProjectsPageQueryV1()
    }

</script>

<template>
    <div>
        <CorporateNavigation ActivePage="dashboard"></CorporateNavigation>

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <small class="text-muted">Enterprise Plan: {{rm.ScenarioName}}</small>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Projects</h1>
                </div>
                <div class="col-lg-6 d-flex flex-column justify-content-end">
                    <ScenarioNavigation @scheduled="onScheduled" />
                </div>
            </div>

            <hr />

            <div class="row mt-5">
                <div class="col-lg-6 col-md-12">
                    <div class="card">
                        <div class="card-header">
                            <div class="float-right">
                                <div class="dropdown d-inline mr-2">
                                    <button data-toggle="dropdown" class="btn btn-secondary btn-sm">Legend</button>
                                    <div class="dropdown-menu dropdown-menu-right" role="menu">
                                        <span v-for="p in rm.Portfolios" :key="p.Id" class="dropdown-item-text">
                                            <i class="fa-solid fa-circle" :style="{'color': p.Color}"></i> {{p.Name}}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <h5>Efficient Frontier</h5>
                        </div>
                        <div class="card-body chart-container">
                            <div id="Chart-EfficientFrontier" style="max-width: 100%; height: 100%; overflow: auto;"></div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6 col-md-12">
                    <div class="card">
                        <div class="card-header">
                            <div class="float-right">
                                <div class="dropdown d-inline mr-2">
                                    <button data-toggle="dropdown" class="btn btn-secondary btn-sm">Legend</button>
                                    <div class="dropdown-menu dropdown-menu-right" role="menu">
                                        <span v-for="p in rm.Portfolios" :key="p.Id" class="dropdown-item-text">
                                            <i class="fa-solid fa-circle" :style="{'color': p.Color}"></i> {{p.Name}}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <h5>Prioritization Matrix</h5>
                        </div>
                        <div class="card-body chart-container">
                            <div id="Chart-PrioritizationMatrix" style="max-width: 100%; height: 100%; overflow: auto;"></div>
                        </div>
                    </div>
                </div>

            </div>

            <br /><br />

            <div>
                <div class="table-responsive">
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th class="align-middle" style="min-width: 300px; width: 300px; max-width: 300px;">Project</th>
                                <th class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px;">P&L</th>
                                <th class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px;">Portfolio</th>
                                <th class="text-center align-middle" style="min-width: 200px; width: 200px; max-width: 200px;">Cost of value point <br /> ({{rm.CurrencyLabel}})</th>
                                <th class="text-right align-middle" style="min-width: 250px; width: auto;">Costs <br /> ({{rm.CurrencyLabel}})</th>
                                <th class="align-middle" style="min-width: 250px; width: auto; ">Value <br /> (0-100)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="a in projectsView" :key="a.Id">
                                <td style="min-width: 300px; width: 300px; max-width: 300px; ">
                                    <a class="text-primary" v-sanitizedHref="`/#/portfolio/alternative/dashboard/${a.PortfolioWorkspaceId}/${a.PortfolioTchProjectId}/${a.Id}`">
                                        {{a.Name}}
                                    </a>
                                </td>
                                <td class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px; ">
                                    <a v-if="a.HasActivePlan" class="text-primary" v-sanitizedHref="`/#/q/project/pnl/${a.PortfolioWorkspaceId}/${a.PortfolioTchProjectId}/${a.Id}`">
                                        View
                                    </a>
                                    <a v-else class="text-danger" v-sanitizedHref="`/q/project/summary/${a.PortfolioWorkspaceId}/${a.PortfolioTchProjectId}/${a.Id}`">
                                        Not available
                                    </a>
                                </td>
                                <td class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px; ">
                                    <a class="text-primary" v-sanitizedHref="`/#/portfolio-plan/selection/${a.PortfolioWorkspaceId}/${a.PortfolioTchProjectId}/${a.ScenarioId}`">
                                        {{a.PortfolioName}}
                                    </a>
                                </td>
                                <td class="text-center align-middle" style="min-width: 200px; width: 200px; max-width: 200px;">
                                    {{fmtNum(a.CostOfValuePoint)}}
                                </td>
                                <td class="text-right align-middle" style="min-width: 250px; width: auto; ">
                                    <div class="progress justify-content-end" style="height: 30px;">
                                        <div class="progress-bar bg-danger" role="progressbar" :style="{width: a.NormalizedTotalCost + '%'}">
                                            {{fmtNum(a.TotalCost)}}
                                        </div>
                                    </div>
                                </td>
                                <td class="align-middle" style="min-width: 250px; width: auto;">
                                    <div class="progress" style="height: 30px;">
                                        <div class="progress-bar bg-success" role="progressbar" :style="{width: a.TotalValue + '%'}">
                                            {{fmtNum(a.TotalValue)}}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

